<template>
    <div>
        <div v-if="hasTwinInCart" class="card">
            {{ lang('Du har redan ett tvillingabonnemang i din varukorg', 'You already have a twin package in your cart') }}
        </div>
        <transition v-else name="cart">
            <div
                v-show="! productsAddedToCart"
                class="card has-footer"
            >
                <h1 class="font-regular">{{ twin.name }}</h1>
                <div class="text-md text-neutral-dark mb-lg md:mb-5xl lg:max-w-lg">
                    {{ twin.description }}
                </div>

                <setting-with-text
                    v-if="! twin.is_collective"
                    :setting="monthlyPrice"
                    :title="lang('Månadskostnad', 'Monthly price')"
                    :description="lang('Tjänstens månatliga kostnad', 'The service monthly price')"
                />

                <setting-with-radio
                    v-if="twinCampaigns.length"
                    v-model="selectedTwinCampaign"
                    :options="twinCampaignOptions"
                    :title="lang('HD-box','HD box')"
                    :description="translation('addon_tv_twin_hd_box_expl')"
                    :class="{ 'border-b-0': selectedTwinCampaign }"
                />
                <setting-with-text
                    v-else-if="twinSelected && twin.hd_box.twin_is_collective"
                    :title="lang('HD-box','HD box')"
                >
                    <div class="font-bold text-sm text-neutral-darker lg:w-2/5">
                        <div class="bg-neutral-lighter rounded-lg py-md px-lg flex items-center justify-start">
                            <div class="text-lg text-success mr-lg">
                                <font-awesome-icon icon="check-circle" />
                            </div>
                            <div class="max-w-xs text-sm font-semibold">
                                <span>{{ lang('Du har redan en HD-box från Ownit','You already own a HD box from ownit') }}!</span>
                            </div>
                        </div>
                    </div>
                </setting-with-text>

                <setting-with-checkbox
                    v-if="hardDrive && (selectedTwinCampaign || (twinSelected && ! twinCampaigns.length))"
                    v-model="hardDriveSelected"
                    layout="small"
                    :options="[{
                        value: 1,
                        name: 'hardDriveSelected',
                        label: hardDrive.name,
                        valuePrice: format_sek(hardDrive.price),
                    }]"
                    :title="lang('Inspelningsbar HD-box', 'Recordable HD box')"
                    :description="hardDrive.description"
                />

                <configuration-footer
                    :validates="canAddToCart"
                    :monthly-price="monthlyPrice"
                    :one-time-cost="oneTimeCost"
                    @add="addToCart"
                />
            </div>
        </transition>
        <products-added-to-cart v-show="productsAddedToCart">
            {{ lang('Dina tillval är tillagda i varukorgen','Your addons have been added to the cart') }}
        </products-added-to-cart>
    </div>
</template>

<script>
    import ConfigurationFooter from '../ConfigurationFooter';
    import SettingWithCheckbox from '../Settings/SettingWithCheckbox';
    import SettingWithRadio from '../Settings/SettingWithRadio';
    import SettingWithText from '../Settings/SettingWithText';
    import ProductsAddedToCart from '../ProductsAddedToCart';

    export default {
        components: {
            ConfigurationFooter,
            SettingWithCheckbox,
            SettingWithRadio,
            SettingWithText,
            ProductsAddedToCart,
        },
        props: {
            configurationData: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                twin: this.configurationData.twin,
                twinCampaigns: this.configurationData.twin.campaigns,
                hardDrive: this.configurationData.hardDriveForTwin,
                hasTwinInCart: this.configurationData.hasTwinInCart,
                hardDriveSelected: false,
                selectedTwinCampaign: null,
                wantsToOrderTwin: true,
                productsAddedToCart: false,
            };
        },
        computed: {
            twinCampaignOptions() {
                return this.twinCampaigns.map((campaign) => {
                    return {
                        value: campaign.article_id,
                        name: 'twinCampaign',
                        placeholder: campaign.name,
                        price_label: format_sek(campaign.start_fee),
                    };
                });
            },
            twinSelected() {
                if (this.wantsToOrderTwin && !this.twin.campaigns.length) {
                    return true;
                }

                return !! this.selectedTwinCampaign;
            },
            canAddToCart() {
                // If user wants to order a Twin, a selected campaign (ie. for the hd box) must be selected
                // UNLESS the twin package is collective
                if (this.wantsToOrderTwin && (this.selectedTwinCampaign === null && !this.twin.is_collective)) {
                    return false;
                }

                return this.twinSelected;
            },
            monthlyPrice() {
                if (this.twin.is_collective) {
                    return format_sek_per_month(0);
                }

                if (this.twin.monthly_price) {
                    return format_sek_per_month(this.twin.monthly_price);
                }

                return '-';
            },
            oneTimeCost() {
                let oneTimeCost = 0;

                if (this.selectedTwinCampaignObject) {
                    oneTimeCost = this.selectedTwinCampaignObject.start_fee;
                }

                if (this.hardDriveSelected) {
                    oneTimeCost += this.hardDrive.price;
                }

                return format_sek(oneTimeCost);
            },
            selectedTwinCampaignObject() {
                return this.twin.campaigns.find((campaign) => {
                    return campaign.article_id === this.selectedTwinCampaign;
                });
            },
        },
        methods: {
            async addToCart() {
                const articles = [];

                // A non-collective twin package always has campaigns for the hd box
                if (this.twin.campaigns.length) {
                    const selectedCampaign = this.twin.campaigns.find((campaign) => {
                        return campaign.article_id === this.selectedTwinCampaign;
                    });

                    const twinArticle = {
                        articleClass: selectedCampaign.article_class,
                        articleId: selectedCampaign.article_id,
                        subItems: {},
                        parameters: {
                            hardDriveSelected: this.hardDriveSelected,
                        },
                    };

                    const hdBox = selectedCampaign.hd_boxes.length > 0 ? selectedCampaign.hd_boxes[0] : null;
                    if (hdBox) {
                        twinArticle['subItems']['hdBox'] = {
                            articleClass: hdBox.article_class,
                            articleId: hdBox.article_id,
                        };
                    }
                    articles.push(twinArticle);
                }

                // Twin can be collective so in this case just add the package
                if (this.twin.is_collective) {
                    articles.push({
                        articleClass: this.twin.article_class,
                        articleId: this.twin.article_id,
                        parameters: {
                            hardDriveSelected: this.hardDriveSelected,
                        },
                    });
                }

                const response = await axios.post(route('front.api.cart-items.store'), {
                    articles,
                });

                EventBus.$emit('cartDataUpdated', response.data);
                this.productsAddedToCart = true;
            },
        },
    };
</script>
